import { useState, useEffect } from 'react';
import { Copy, KeyRound, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader, SectionCard } from '@/components/admin/page';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';

interface CodeResponse {
  code: string;
  expiresAt: string;
  expiresInSec: number;
}

interface Member {
  userId: string;
  role: string;
  email: string;
  displayName: string | null;
}

const SELF_KEY = '__self__';

export function PairingPage() {
  const { t } = useI18n();
  const [code, setCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [meId, setMeId] = useState<string | null>(null);
  const [targetUserId, setTargetUserId] = useState<string>(SELF_KEY);

  useEffect(() => {
    void (async () => {
      try {
        const me = await api<{ user: { id: string } }>('/v1/auth/me');
        setMeId(me.user.id);
        const list = await api<{ members: Member[] }>('/v1/members');
        setMembers(list.members);
      } catch {
        // 멤버 목록 fetch 실패해도 본인 발급은 가능 — 조용히 skip
      }
    })();
  }, []);

  const issueCode = async () => {
    setLoading(true);
    setError(null);
    setCopied(false);
    try {
      const payload = targetUserId === SELF_KEY ? {} : { targetUserId };
      const body = await api<CodeResponse>('/v1/pairing/codes', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setCode(body.code);
      setExpiresAt(new Date(body.expiresAt).getTime());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const otherMembers = members.filter((m) => m.userId !== meId);

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const left = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setRemaining(left);
      if (left === 0) { setCode(null); setExpiresAt(null); }
    };
    tick();
    const tm = setInterval(tick, 1000);
    return () => clearInterval(tm);
  }, [expiresAt]);

  const copy = () => {
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const timeStr = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`;

  return (
    <div>
      <PageHeader
        title={t('pairing.title')}
        description={t('pairing.desc')}
        actions={!code && <Button onClick={issueCode} disabled={loading}><KeyRound />{loading ? t('common.issuing') : t('pairing.title')}</Button>}
      />

      {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

      <SectionCard title={t('pairing.section')} description={t('pairing.sectionDesc')}>
        {otherMembers.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border bg-muted/10 px-3 py-2">
            <label htmlFor="pairing-target" className="text-sm font-medium">配对对象</label>
            <select
              id="pairing-target"
              value={targetUserId}
              onChange={(e) => setTargetUserId(e.target.value)}
              className="rounded-md border bg-background px-2 py-1 text-sm"
            >
              <option value={SELF_KEY}>{t('pairing.targetSelf')}</option>
              {otherMembers.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {(m.displayName ?? m.email)}{m.displayName ? ` (${m.email})` : ''}
                </option>
              ))}
            </select>
            <span className="text-xs text-muted-foreground">{t('pairing.targetHint')}</span>
          </div>
        )}
        {code ? (
          <Card className="border-dashed border-primary/40 bg-primary/10">
            <CardContent className="p-6">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('pairing.issued')}</div>
              <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <code className="select-all rounded-xl bg-background px-4 py-3 text-4xl font-semibold tracking-[0.22em] text-foreground">{code}</code>
                <Button variant="outline" onClick={copy}><Copy />{copied ? t('common.copied') : t('common.copy')}</Button>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                {remaining > 0
                  ? <Badge variant="success">{t('pairing.remaining', { time: timeStr })}</Badge>
                  : <Badge variant="destructive">{t('common.expired')}</Badge>}
                <Button variant="link" className="h-auto p-0" onClick={issueCode} disabled={loading}><RefreshCw className={loading ? 'animate-spin' : ''} />{t('pairing.newCode')}</Button>
              </div>
              <ol className="mt-5 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                <li>{t('pairing.step1')}</li>
                <li>{t('pairing.step2')}</li>
                <li>{t('pairing.step3')}</li>
              </ol>
            </CardContent>
          </Card>
        ) : (
          <div className="rounded-xl border border-dashed bg-muted/20 p-8 text-center">
            <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <KeyRound className="size-5" />
            </div>
            <div className="text-lg font-semibold">{t('pairing.emptyTitle')}</div>
            <p className="mt-2 text-sm text-muted-foreground">{t('pairing.emptyDesc')}</p>
            <Button className="mt-5" onClick={issueCode} disabled={loading}>{loading ? t('common.issuing') : t('pairing.title')}</Button>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
